import {useState, useCallback, useMemo, useRef, useEffect} from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {AddButtonProps} from 'shells/card/card-widget/card-widget';
import {CardWidgetEmptyState} from 'shells/empty-states/card-widget-empty-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SearchBar} from 'shells/search-bar/search-bar';
import {SelectMenu} from 'shells/select-menu/select-menu';
import {Body} from 'shells/typography/body';
import {useDebounce} from 'shells/utils/hooks/use-debounce';

import type {
    ContactCreateInput,
    ContactUpdateInput,
    GetRelatedContacts_account_contacts_edges as ContactEdges,
    PeepMapCreateInput,
    WidgetFragment,
    PeepRelatedPeepSortType,
} from 'nutshell-graphql-types';

import {useCreatePeepMap} from '../../../graphql/hooks/use-create-peep-map';
import {useDeletePeepMap} from '../../../graphql/hooks/use-delete-peep-map';
import {useUpdatePeepMap} from '../../../graphql/hooks/use-update-peep-map';
import {
    getFilterString,
    getSortByOptions,
    getIsEmpty,
    getPeepEmptyStateProps,
} from '../../../shared/related-peeps-widget/helpers';
import {TabSearchFilterSection} from '../../../shared/related-peeps-widget/tab-search-filter-section';
import type {RelatedPeepsTab} from '../../../shared/related-peeps-widget/types';
import type {UpdateContactRelationshipMapInput} from '../../../types';

import {CollapsibleCardWidget} from '../../../../core/card/collapsible-card-widget';
import {useGetSessionUser} from '../../../../core/use-get-session-user';
import {EmailModalWrapper} from '../../../../core-entity/components/email-modal-wrapper';
import {WidgetSeeAllFooter} from '../../../../core-entity/components/widget-see-all-footer';
import {useCreateContactForCoreEntity} from '../../../../core-entity/graphql/hooks/use-create-contact-for-core-entity';
import {ContactRow} from '../../../../core-entity/rows/peep-rows/contact-row';

import {accountRefetchForEvent} from '../../hooks/use-account-update-refetch';

import {ContactForm} from './contact-form';
import {useGetRelatedContacts} from './graphql/hooks/use-get-related-contacts';
import {useUpdateRelatedContact} from './graphql/hooks/use-update-related-contact';
import {getToolbarOptions} from './helpers';

import styles from './related-contacts-widget.module.css';

interface Props {
    widget: WidgetFragment;
    accountId: string;
}

type PinnedContactEdge = Omit<ContactEdges, '__typename'>;

export function RelatedContactsWidget(props: Props) {
    const [selectedTab, setSelectedTab] = useState<RelatedPeepsTab>('current');
    const [error, setError] = useState<string | null | undefined>(null);
    const [isCreatingContact, setIsCreatingContact] = useState<boolean>(false);
    const [emailModalSeededEmailAddress, setEmailModalSeededEmailAddress] = useState<
        string | null | undefined
    >(null);
    const [isEditingContactId, setIsEditingContactId] = useState<string | null>(null);
    const [showSearch, setShowSearch] = useState<boolean>(false);
    const [currentSearch, setCurrentSearch] = useState<string | null>(null);
    const [formerSearch, setFormerSearch] = useState<string | null>(null);
    const [showSortBy, setShowSortBy] = useState<boolean>(false);
    const [currentSortBy, setCurrentSortBy] = useState<PeepRelatedPeepSortType | null>(null);
    const [formerSortBy, setFormerSortBy] = useState<PeepRelatedPeepSortType | null>(null);
    const [pinnedContacts, setPinnedContacts] = useState<PinnedContactEdge[]>([]);
    const [newlyAddedContactId, setNewlyAddedContactId] = useState<string | null>(null);

    const sortByButtonRef = useRef<HTMLButtonElement | null | undefined>(null);

    const debouncedCurrentSearch = useDebounce(currentSearch, 300);
    const debouncedFormerSearch = useDebounce(formerSearch, 300);

    const {user, isLoading: isLoadingUser} = useGetSessionUser();
    const {
        contacts: currentContacts,
        totalCount: currentUnfilteredCount,
        isLoading: isLoadingCurrentContacts,
        isLoadingMore: isLoadingMoreCurrentContacts,
        isRefreshing: isRefreshingCurrentContacts,
        isRefetching: isRefetchingCurrentContacts,
        isExpanded: isCurrentExpanded,
        hasNextPage: hasNextPageCurrentContacts,
        fetchAllContacts: fetchAllCurrentContacts,
        refetch: refetchCurrentContacts,
    } = useGetRelatedContacts(props.accountId, false, false, debouncedCurrentSearch, currentSortBy);
    const {
        contacts: formerContacts,
        totalCount: formerUnfilteredCount,
        isLoading: isLoadingFormerContacts,
        isLoadingMore: isLoadingMoreFormerContacts,
        isRefreshing: isRefreshingFormerContacts,
        isRefetching: isRefetchingFormerContacts,
        isExpanded: isFormerExpanded,
        hasNextPage: hasNextPageFormerContacts,
        fetchAllContacts: fetchAllFormerContacts,
        refetch: refetchFormerContacts,
    } = useGetRelatedContacts(props.accountId, true, false, debouncedFormerSearch, formerSortBy);

    const refetchContacts = useCallback(() => {
        refetchCurrentContacts();
        refetchFormerContacts();
    }, [refetchCurrentContacts, refetchFormerContacts]);

    useEffect(() => {
        const handleAccountUpdated = (event: Event) => {
            const customEvent = event as CustomEvent<{type: string}>;
            if (
                customEvent.detail.type === 'accountContactsUpdated' ||
                customEvent.detail.type === 'accountEnrichmentUpdated'
            ) {
                refetchCurrentContacts();
                refetchFormerContacts();
            }
        };

        window.addEventListener('accountUpdated', handleAccountUpdated);

        return () => window.removeEventListener('accountUpdated', handleAccountUpdated);
    }, [refetchCurrentContacts, refetchFormerContacts]);

    const {deletePeepMap, isLoading: isLoadingDeletePeepMap} = useDeletePeepMap();
    const {updatePeepMap, isLoading: isLoadingUpdatePeepMap} = useUpdatePeepMap();
    const {createPeepMap, isLoading: isLoadingCreatePeepMap} = useCreatePeepMap();
    const {createContactForCoreEntity, isLoading: isLoadingCreateContact} =
        useCreateContactForCoreEntity();
    const {updateRelatedContact, isLoading: isLoadingUpdateRelatedContact} =
        useUpdateRelatedContact();

    const dispatchContactsUpdated = useCallback(() => {
        accountRefetchForEvent('accountContactsUpdated');
    }, []);

    const clearPinnedContacts = useCallback(() => {
        setPinnedContacts([]);
    }, []);

    const pinContact = useCallback((contact: PinnedContactEdge) => {
        setPinnedContacts((prevPinnedContacts) => [
            contact,
            ...prevPinnedContacts.filter(
                (pinnedContact) => pinnedContact.node.id !== contact.node.id
            ),
        ]);
    }, []);

    const removePinnedContact = useCallback((contactId: string) => {
        setPinnedContacts((prevPinnedContacts) =>
            prevPinnedContacts.filter((pinnedContact) => pinnedContact.node.id !== contactId)
        );
    }, []);

    const handleDeleteContactMap = useCallback(
        (contactId: string) => {
            deletePeepMap({
                contact: contactId,
                account: props.accountId,
            })
                .then(() => {
                    removePinnedContact(contactId);
                    dispatchContactsUpdated();
                    refetchContacts();
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [
            deletePeepMap,
            props.accountId,
            removePinnedContact,
            dispatchContactsUpdated,
            refetchContacts,
        ]
    );

    const handleUpdateContactMap = useCallback(
        (input: UpdateContactRelationshipMapInput) => {
            updatePeepMap({
                contact: input.contact,
                account: props.accountId,
                relationshipType: input?.relationshipType,
            })
                .then(() => {
                    removePinnedContact(input.contact);
                    dispatchContactsUpdated();
                    refetchContacts();
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [
            updatePeepMap,
            props.accountId,
            removePinnedContact,
            dispatchContactsUpdated,
            refetchContacts,
        ]
    );

    const handleAddContactMap = useCallback(
        (input: PeepMapCreateInput) => {
            createPeepMap(input)
                .then((result) => {
                    const contact = result?.data?.peepMapCreate?.contact;
                    if (contact) {
                        pinContact({
                            node: contact,
                            isFormer: false,
                            relationshipNote: null,
                        });
                        setNewlyAddedContactId(contact.id);
                    }
                    dispatchContactsUpdated();
                    refetchContacts();
                    setIsCreatingContact(false);
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [createPeepMap, pinContact, dispatchContactsUpdated, refetchContacts]
    );

    const handleCreateContact = useCallback(
        (input: ContactCreateInput) => {
            createContactForCoreEntity(input)
                .then((result) => {
                    const contact = result?.data?.contactCreate?.contact;
                    if (contact) {
                        pinContact({
                            node: contact,
                            isFormer: false,
                            relationshipNote: input.contact.accounts?.[0]?.relationship ?? null,
                        });
                        setNewlyAddedContactId(contact.id);
                    }
                    dispatchContactsUpdated();
                    refetchContacts();
                    setIsCreatingContact(false);
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [createContactForCoreEntity, pinContact, dispatchContactsUpdated, refetchContacts]
    );

    const handleUpdateRelatedContact = useCallback(
        (input: ContactUpdateInput) => {
            updateRelatedContact(input)
                .then((result) => {
                    const updatedContact = result?.data?.contactUpdate?.contact;
                    const updatedRelationshipNote =
                        input.contact.accounts?.find((a) => a.id === props.accountId)
                            ?.relationship ?? null;

                    if (updatedContact) {
                        setPinnedContacts((prev) =>
                            prev.map((edge) =>
                                edge.node.id === updatedContact.id
                                    ? {
                                          ...edge,
                                          node: {...edge.node, ...updatedContact},
                                          relationshipNote: updatedRelationshipNote,
                                      }
                                    : edge
                            )
                        );
                    }

                    dispatchContactsUpdated();
                    refetchContacts();
                    setIsEditingContactId(null);
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [updateRelatedContact, dispatchContactsUpdated, refetchContacts, props.accountId]
    );

    const handleOpenEmailModal = (emailAddress: string) => {
        setEmailModalSeededEmailAddress(emailAddress);
    };

    const handleSeeAllCurrentContacts = useCallback(() => {
        fetchAllCurrentContacts();
    }, [fetchAllCurrentContacts]);

    const handleSeeAllFormerContacts = useCallback(() => {
        fetchAllFormerContacts();
    }, [fetchAllFormerContacts]);

    const handleTabChange = useCallback(
        (newTab: RelatedPeepsTab) => {
            clearPinnedContacts();
            setCurrentSortBy(null);
            setCurrentSearch(null);
            setFormerSortBy(null);
            setFormerSearch(null);
            setShowSearch(false);
            setSelectedTab(newTab);
            setIsCreatingContact(false);
        },
        [clearPinnedContacts]
    );

    const handleSortByChange = useCallback(
        (optionValue: PeepRelatedPeepSortType | null) => {
            clearPinnedContacts();
            if (selectedTab === 'current') {
                setCurrentSortBy(optionValue);
            } else {
                setFormerSortBy(optionValue);
            }
            setShowSortBy(false);
        },
        [clearPinnedContacts, selectedTab]
    );
    const handleSearchChange = useCallback(
        (value: string) => {
            clearPinnedContacts();
            if (selectedTab === 'current') {
                setCurrentSearch(value);
            } else {
                setFormerSearch(value);
            }
        },
        [clearPinnedContacts, selectedTab]
    );

    const contactsToRender = useMemo(
        () => (selectedTab === 'current' ? currentContacts : formerContacts),
        [selectedTab, currentContacts, formerContacts]
    );

    const contactEdgesToRender = useMemo(() => {
        if (!contactsToRender || pinnedContacts.length === 0) {
            return contactsToRender?.edges ?? [];
        }

        const backendEdgesById = new Map(
            contactsToRender.edges.map((contact) => [contact.node.id, contact])
        );
        const pinnedContactIds = new Set(pinnedContacts.map((contact) => contact.node.id));

        // Get the backend edges for the pinned contacts if possible, otherwise use the pinned contact data
        const mergedPinnedContacts = pinnedContacts.map(
            (contact) => backendEdgesById.get(contact.node.id) ?? contact
        );
        const remainingContacts = contactsToRender.edges.filter(
            (contact) => !pinnedContactIds.has(contact.node.id)
        );

        return [...mergedPinnedContacts, ...remainingContacts];
    }, [contactsToRender, pinnedContacts]);

    // Get a deduplicated list of all contact IDs
    const allContactsIds = useMemo(
        () =>
            Array.from(
                new Set([
                    ...(currentContacts?.edges?.map((contact) => contact.node.id) ?? []),
                    ...(formerContacts?.edges?.map((contact) => contact.node.id) ?? []),
                    ...pinnedContacts.map((contact) => contact.node.id),
                ])
            ),
        [currentContacts, formerContacts, pinnedContacts]
    );

    // Reset the newlyAddedContactId once it has flashed once
    // so that it doesn't flash again if the contact is updated while still pinned
    useEffect(() => {
        if (!newlyAddedContactId) {
            return;
        }

        const isRendered = contactEdgesToRender.some(
            (contact) => contact.node.id === newlyAddedContactId
        );

        if (!isRendered) {
            return;
        }

        const frame = window.requestAnimationFrame(() => {
            setNewlyAddedContactId((currentId) =>
                currentId === newlyAddedContactId ? null : currentId
            );
        });

        return () => window.cancelAnimationFrame(frame);
    }, [newlyAddedContactId, contactEdgesToRender]);

    const isLoading =
        isLoadingUser ||
        (selectedTab === 'current'
            ? isLoadingCurrentContacts && !showSearch
            : isLoadingFormerContacts && !showSearch);
    const isReloading =
        isLoadingDeletePeepMap ||
        isLoadingUpdatePeepMap ||
        isRefetchingCurrentContacts ||
        isRefetchingFormerContacts ||
        isRefreshingCurrentContacts ||
        isRefreshingFormerContacts;
    const isLoadingMore =
        selectedTab === 'current' ? isLoadingMoreCurrentContacts : isLoadingMoreFormerContacts;

    const isEmpty = getIsEmpty(
        selectedTab,
        currentUnfilteredCount,
        formerUnfilteredCount,
        isCreatingContact,
        isReloading,
        showSearch || showSortBy
    );

    const showHeaderSpinner = isReloading && !isEmpty;

    const userCanUseSms = user && user.permissions && user.permissions.canUseSms;
    const hasConnectedPhoneIntegration = Boolean(
        user &&
            (user.phoneIntegrations.some(
                (integration) => integration && integration.isEnabledForUser
            ) ||
                (user.clickToCallIntegration && user.clickToCallIntegration.isEnabled))
    );

    const emptyStateProps = getPeepEmptyStateProps('contact', isEmpty);

    const addButtonProps: AddButtonProps = {
        variant: 'singular',
        onClick: () => {
            setIsCreatingContact(true);
            setSelectedTab('current');
        },
        label: '+ Add person',
        isDisabled: isCreatingContact,
    };

    return (
        <>
            <CollapsibleCardWidget
                widget={props.widget}
                isLoading={isLoading}
                addButtonProps={addButtonProps}
                emptyStateProps={emptyStateProps}
                errorProps={{
                    error: error,
                    setError: setError,
                }}
                hasBorder={true}
                showHeaderSpinner={showHeaderSpinner}
                tabProps={{
                    headerContent: (
                        <TabSearchFilterSection
                            selectedTab={selectedTab}
                            onTabChange={handleTabChange}
                            formerCount={formerUnfilteredCount}
                            currentCount={currentUnfilteredCount}
                            onSearchClicked={() => {
                                if (currentSearch !== null || formerSearch !== null) {
                                    clearPinnedContacts();
                                }
                                setShowSearch((prevShowSearch) => !prevShowSearch);
                                setCurrentSearch(null);
                                setFormerSearch(null);
                            }}
                            onFilterClicked={() => {
                                setShowSortBy((prevShowSortBy) => !prevShowSortBy);
                            }}
                            isSearching={showSearch}
                            hasSort={showSortBy || currentSortBy !== null || formerSortBy !== null}
                            getSortByButtonRef={(ref) => {
                                sortByButtonRef.current = ref;
                            }}
                        />
                    ),
                }}
            >
                <div className={styles['scroll-container']}>
                    {showSearch && (
                        <div className='pad-12 background-offwhite flex flex-dir-col gap-8'>
                            <SearchBar
                                placeholder='Search by name or location…'
                                value={
                                    selectedTab === 'current'
                                        ? currentSearch ?? ''
                                        : formerSearch ?? ''
                                }
                                onChange={handleSearchChange}
                                autoFocus={true}
                                hasBorder={true}
                            />
                            <Body size='small' color='light'>
                                {getFilterString(
                                    currentSortBy,
                                    formerSortBy,
                                    selectedTab,
                                    currentUnfilteredCount,
                                    formerUnfilteredCount,
                                    contactEdgesToRender.length,
                                    'contact'
                                )}
                            </Body>
                        </div>
                    )}
                    <div className='mt-8'>
                        {isCreatingContact && (
                            <ContactForm
                                accountId={props.accountId}
                                onClose={() => setIsCreatingContact(false)}
                                mutationProps={{
                                    variant: 'create',
                                    onCreate: handleCreateContact,
                                    onAdd: handleAddContactMap,
                                    alreadyAddedContactIds: allContactsIds,
                                }}
                                isSaving={isLoadingCreateContact || isLoadingCreatePeepMap}
                            />
                        )}
                        {!contactEdgesToRender.length && (
                            <CardWidgetEmptyState {...getPeepEmptyStateProps('contact', false)} />
                        )}
                        {contactEdgesToRender.map((contact) => {
                            if (isEditingContactId === contact.node.id) {
                                return (
                                    <ContactForm
                                        accountId={props.accountId}
                                        onClose={() => setIsEditingContactId(null)}
                                        mutationProps={{
                                            variant: 'update',
                                            onUpdate: handleUpdateRelatedContact,
                                            temporaryContact: {
                                                id: contact.node.id,
                                                name: contact.node.name,
                                                description: contact.node.description,
                                                phoneNumber:
                                                    contact.node.phones?.primary?.value
                                                        ?.numberFormattedWithCountryCode,
                                                email: contact.node.primaryEmailAddress,
                                                jobTitle: contact.relationshipNote,
                                            },
                                        }}
                                        isSaving={isLoadingUpdateRelatedContact}
                                    />
                                );
                            }
                            return (
                                <ContactRow
                                    key={contact.node.id}
                                    contactId={contact.node.id}
                                    name={contact.node.name}
                                    contactUrl={contact.node.htmlUrlPath}
                                    description={contact.node.description}
                                    role={contact.relationshipNote}
                                    phone={contact.node.phones?.primary?.value}
                                    phoneCallerType={user?.phoneCallerType}
                                    userCanUseSms={userCanUseSms}
                                    sendFromNutshellEnabled={user?.sendFromNutshellEnabled}
                                    email={contact.node.primaryEmailAddress}
                                    lastContactedTime={contact.node.lastContactedTime}
                                    isDuplicate={contact.node.hasDuplicates}
                                    isFormer={contact.isFormer}
                                    isPrimary={contact.node.id === currentContacts?.primary?.id}
                                    showIsPrimaryIcon={currentContacts?.edges?.length !== 1}
                                    avatarUrl={contact.node.avatarUrl}
                                    parentEntityName='Accounts'
                                    onOpenEmailModal={handleOpenEmailModal}
                                    removeContact={handleDeleteContactMap}
                                    setError={setError}
                                    hasConnectedPhoneIntegration={hasConnectedPhoneIntegration}
                                    prependToolbarOptions={getToolbarOptions(
                                        contact.node.id,
                                        contact.isFormer,
                                        contact.node.id === currentContacts?.primary?.id,
                                        handleUpdateContactMap,
                                        setIsEditingContactId
                                    )}
                                    successFlashKey={
                                        newlyAddedContactId === contact.node.id
                                            ? contact.node.id
                                            : undefined
                                    }
                                />
                            );
                        })}
                        {isLoadingMore && <LoadingPage padding={8} />}
                    </div>
                </div>
                <WidgetSeeAllFooter
                    seeAllProps={{
                        isExpanded:
                            selectedTab === 'current' ? isCurrentExpanded : isFormerExpanded,
                        hasMore:
                            selectedTab === 'current'
                                ? hasNextPageCurrentContacts
                                : hasNextPageFormerContacts,
                        term: `${selectedTab} people`,
                        onClick:
                            selectedTab === 'current'
                                ? handleSeeAllCurrentContacts
                                : handleSeeAllFormerContacts,
                    }}
                    listLink={`/people?filter[accounts][data][0][data]=${props.accountId}&view=list`}
                    listLinkItemCount={
                        selectedTab === 'current' ? currentUnfilteredCount : formerUnfilteredCount
                    }
                />
            </CollapsibleCardWidget>
            {emailModalSeededEmailAddress && (
                <EmailModalWrapper
                    entityId={props.accountId}
                    postModalOpen={() => {
                        setEmailModalSeededEmailAddress(null);
                    }}
                    seededWithEmailAddress={emailModalSeededEmailAddress}
                />
            )}
            {showSortBy && (
                <SelectMenu
                    anchor={sortByButtonRef.current}
                    onClose={() => setShowSortBy(false)}
                    options={getSortByOptions(
                        currentSortBy,
                        formerSortBy,
                        selectedTab,
                        handleSortByChange
                    )}
                    location='bottom-left'
                />
            )}
        </>
    );
}
